/**
 * Task Edit Modal
 * Trello-style modal for editing task cards with labels and due dates
 * Labels are consistent - each color has one name across all cards
 */

import { useState, useEffect, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { DatePickerDropdown } from "./DatePickerDropdown";
import { useEscapeKey } from "@/common/hooks/useEscapeKey";
import { useModalOverlayClick } from "@/common/hooks/useModalOverlayClick";
import { useClock24Hour } from "@/common/hooks/useClock24Hour";
import { useSession } from "@/features/auth/hooks/useSession";
import { LABEL_COLORS, DEFAULT_LABEL_COLOR, getAvatarColorPair } from "../shared/colors";
import { migrateLabels } from "./helpers";

/** A task may carry up to this many labels. */
const MAX_TASK_LABELS = 5;
import {
  localDateTimeToISO,
  dueAtToLocalDate,
  dueAtToLocalTime,
} from "./utils/date";
import { TaskTimePicker } from "./components/TaskTimePicker";
import { DeleteConfirmationModal } from "./components/DeleteConfirmationModal";
import { UnsavedChangesGuardModal } from "@/common/components/UnsavedChangesGuardModal";
import { StatusDropdown } from "./components/StatusDropdown";
import { LabelDropdown } from "./components/LabelDropdown";
import { TaskViewOnlyBody } from "./components/TaskViewOnlyBody";
import type {
  TaskNote,
  AssignedUser,
  TaskObject,
  TaskEditModalProps,
} from "./taskEditModal.types";
import "./TaskEditModal.css";

function relativeTime(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function avatarInitials(name: string): string {
  return name.split(/\s+/).filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase() || '?';
}

const EMPTY_MEMBERS: never[] = [];

export function TaskEditModal({
  task,
  allTasks,
  conversationMembers = EMPTY_MEMBERS,
  module,
  existingLabels: existingLabelsProp,
  columns = [],
  onUpdateModuleLabels,
  enabledFields,
  isNewCard = false,
  viewOnly = false,
  // columnColor prop intentionally unused — the editor stays uniform gray.
  onClose,
  onSave,
  onDelete,
  onArchive,
}: TaskEditModalProps) {
  const fieldOn = (field: string) => !enabledFields || enabledFields.includes(field);

  const isEditingNoteRef = useRef(false);

  // Unsaved-changes guard (escape-layer contract, issue #3938). isDirtyRef is set
  // by markDirty (wired to the form's onInput/onChange below); handleSafeClose
  // routes Esc / X / backdrop through the confirmation when dirty instead of
  // discarding silently. useEscapeKey already preventDefaults, so the module-pane
  // Esc handler declines and the Tasks pane survives this keypress.
  const isDirtyRef = useRef(false);
  const markDirty = () => {
    isDirtyRef.current = true;
  };
  const [showUnsavedConfirmation, setShowUnsavedConfirmation] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSafeClose = () => {
    if (isDirtyRef.current) {
      setShowUnsavedConfirmation(true);
      return;
    }
    onClose();
  };

  useEscapeKey(() => {
    if (isEditingNoteRef.current) {
      return;
    }
    handleSafeClose();
  });

  // Handle overlay clicks with buffer zone to prevent accidental closures during copy/paste
  // Also prevent closing when editing a note
  const [taskModalContentRef, handleOverlayClick, handleOverlayMouseDown] =
    useModalOverlayClick(() => {
      if (!isEditingNoteRef.current) {
        handleSafeClose();
      }
    }, 30);

  const { profile } = useSession();

  // `modal-open` body class hides the mobile header/footer while the modal is up.
  useEffect(() => {
    document.body.classList.add("modal-open");

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  const [title, setTitle] = useState(task.title || "");
  const [githubUrl, setGithubUrl] = useState(task.githubUrl || "");
  const [dueAt, setDueAt] = useState(dueAtToLocalDate(task.dueAt));
  const [dueTime, setDueTime] = useState(dueAtToLocalTime(task.dueAt));
  const [use24h] = useClock24Hour();
  const [notes, setNotes] = useState<TaskNote[]>(
    Array.isArray(task.notes) ? task.notes : [],
  );
  const [owner, setOwner] = useState<AssignedUser | null>(null);
  // Multi-label (up to MAX_TASK_LABELS). Seeded from the canonical labels array,
  // migrating any legacy single label/labelColor into it (migrateLabels).
  const [labels, setLabels] = useState<Array<{ name: string; color: string }>>(
    () => migrateLabels(task).slice(0, MAX_TASK_LABELS),
  );
  const [customLabelInput, setCustomLabelInput] = useState("");
  const [newLabelColor, setNewLabelColor] = useState(DEFAULT_LABEL_COLOR); // Separate color for "Create New" section
  const [showLabelDropdown, setShowLabelDropdown] = useState(false);
  const [labelDropdownPosition, setLabelDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const labelDropdownRef = useRef<HTMLDivElement>(null);
  const labelButtonRef = useRef<HTMLButtonElement>(null);
  const labelFieldRef = useRef<HTMLDivElement>(null);
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const [showDeleteButton, setShowDeleteButton] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [description, setDescription] = useState(task.description || "");
  const [newNote, setNewNote] = useState("");
  const composerInputRef = useRef<HTMLInputElement>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  // View-only mode editing states
  const [showOwnerPicker, setShowOwnerPicker] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    taskId: string;
    title: string;
  } | null>(null);
  // Status dropdown state
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  const currentColumn = columns.find((col) => col.status === task.status);
  const currentColumnLabel = currentColumn?.label || task.status || "To Do";

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node)
      ) {
        setShowStatusDropdown(false);
      }
    }
    if (showStatusDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showStatusDropdown]);

  const handleStatusChange = (newStatus: string) => {
    onSave({ status: newStatus as TaskObject["status"] });
    setShowStatusDropdown(false);
  };

  // Prefer the live prop (KanbanModule's optimistic localLabels) so that labels
  // created earlier in the same open-modal session are visible immediately without
  // waiting for the server refetch to propagate through module.config.labels.
  const existingLabels: Array<{ name: string; color: string }> =
    existingLabelsProp ?? (module?.config?.labels || []);

  const labelColors = LABEL_COLORS;

  // Use task.id as dependency to avoid infinite loops when task object reference changes
  useEffect(() => {
    setTitle(task.title || "");
    setGithubUrl(task.githubUrl || "");
    setDueAt(dueAtToLocalDate(task.dueAt));
    setDueTime(dueAtToLocalTime(task.dueAt));
    setLabels(migrateLabels(task).slice(0, MAX_TASK_LABELS));
    setDescription(task.description || "");
    setCustomLabelInput("");
    setNewLabelColor(DEFAULT_LABEL_COLOR);
    setNewNote("");
    setEditingNoteId(null);
    setEditingNoteText("");

    const firstAssigned = task.assignedTo?.[0];
    if (firstAssigned) {
      if (typeof firstAssigned === "string") {
        // Old format: look up name from conversationMembers
        const member = conversationMembers.find(
          (m: any) => m.id === firstAssigned,
        );
        if (member) {
          setOwner({
            id: firstAssigned,
            name: member.name || member.email,
          });
        } else {
          // Member not found yet - set with ID only, will show in dropdown when conversationMembers loads
          setOwner({
            id: firstAssigned,
            name: "Loading...",
          });
        }
      } else {
        // New format: prefer fresh conversationMembers name, fall back to embedded snapshot
        const member = conversationMembers.find(
          (m: any) => m.id === firstAssigned.id,
        );
        setOwner({
          id: firstAssigned.id,
          name:
            member?.name || member?.email || firstAssigned.name || "Unknown",
        });
      }
    } else {
      setOwner(null);
    }

    // Migrate legacy notes that may have old format (date/note) or missing author info
    const migratedNotes = (Array.isArray(task.notes) ? task.notes : []).map(
      (note: any) => ({
        id: note.id,
        // Handle old format: 'note' field -> 'content' field
        content: note.content || note.note || "",
        authorId: note.authorId || profile?.id || "",
        authorName: note.authorName || profile?.name || "Unknown",
        // Handle old format: 'date' field -> 'createdAt' field
        createdAt: note.createdAt || note.date || new Date().toISOString(),
        updatedAt: note.updatedAt,
      }),
    );
    setNotes(migratedNotes);
    // task.notes is intentionally NOT a dependency — notes are owned by local
    // state after mount. handleAddNote auto-saves a comment via onSave({ notes }),
    // which flips the parent's task.notes reference; if it were a dependency this
    // effect would re-run and wipe unsaved title/label/description edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    task.id,
    task.title,
    task.dueAt,
    task.label,
    task.labelColor,
    task.labels,
    task.assignedTo,
    task.githubUrl,
    task.description,
    profile?.id,
    profile?.name,
    conversationMembers,
  ]);

  // Update label dropdown position when opened. Anchor to the full-width label
  // FIELD (not the small "+ Add label" button) so the dropdown spans the field
  // and never renders squished; enforce a sensible minimum width.
  useEffect(() => {
    const anchor = labelFieldRef.current ?? labelButtonRef.current;
    if (showLabelDropdown && anchor) {
      const rect = anchor.getBoundingClientRect();
      setLabelDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 280),
      });
    }
  }, [showLabelDropdown]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        labelDropdownRef.current &&
        !labelDropdownRef.current.contains(event.target as Node)
      ) {
        setShowLabelDropdown(false);
        setHoveredLabel(null);
        setShowDeleteButton(null);
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    if (isNewCard) {
      onSave({
        title: trimmedTitle,
        dueAt: dueAt
          ? localDateTimeToISO(dueAt, dueTime || undefined)
          : undefined,
        notes: notes.length > 0 ? notes : undefined,
        assignedTo: owner ? [owner] : undefined,
        labels: labels.length > 0 ? labels : undefined,
        githubUrl: githubUrl.trim() || undefined,
        description: description.trim() || undefined,
      });
      return;
    }

    const patch: any = {};

    if (trimmedTitle !== task.title) patch.title = trimmedTitle;
    const origDueDate = dueAtToLocalDate(task.dueAt);
    const origDueTime = dueAtToLocalTime(task.dueAt);
    if (dueAt !== origDueDate || dueTime !== origDueTime) {
      patch.dueAt = dueAt
        ? localDateTimeToISO(dueAt, dueTime || undefined)
        : null;
    }

    // Check if labels changed (compare by name+color, order-independent).
    const origLabels = migrateLabels(task);
    const sameLabels =
      origLabels.length === labels.length &&
      origLabels.every((o) => labels.some((l) => l.name === o.name && l.color === o.color));
    if (!sameLabels) {
      patch.labels = labels;
      // Clear the legacy single-label fields so they can't shadow/duplicate the
      // canonical array (migrateLabels prefers `labels`, so stale legacy values
      // would otherwise linger on the object).
      if (task.label) patch.label = "";
      if (task.labelColor) patch.labelColor = "";
    }

    const originalNotes = task.notes || [];
    if (JSON.stringify(notes) !== JSON.stringify(originalNotes)) {
      patch.notes = notes.length > 0 ? notes : [];
    }

    const originalOwner = task.assignedTo?.[0];
    const ownerChanged =
      owner?.id !==
      (typeof originalOwner === "string" ? originalOwner : originalOwner?.id);
    if (ownerChanged) {
      patch.assignedTo = owner ? [owner] : [];
    }

    if (githubUrl.trim() !== (task.githubUrl || "")) {
      patch.githubUrl = githubUrl.trim() || "";
    }

    if (description.trim() !== (task.description || "")) {
      patch.description = description.trim() || "";
    }

    isDirtyRef.current = false;
    if (Object.keys(patch).length > 0) {
      onSave(patch);
    }
    onClose();
  };

  // Global Enter key shortcut to save when not focused on input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if user is typing in an input, textarea, or select
      const activeElement = document.activeElement;
      const isTyping =
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.tagName === "SELECT" ||
          activeElement.tagName === "BUTTON");

      // Enter saves when not typing in an input
      if (e.key === "Enter" && !isTyping) {
        e.preventDefault();
        handleSubmit(e as any);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSubmit]);

  const handleViewOnlySave = (
    field: "owner" | "dueAt",
    value: AssignedUser | null | string,
  ) => {
    const patch: any = {};
    if (field === "owner") {
      const owner = value as AssignedUser | null;
      patch.assignedTo = owner ? [owner] : [];
      setOwner(owner);
    } else if (field === "dueAt") {
      patch.dueAt = value
        ? localDateTimeToISO(
            value as string,
            dueAtToLocalTime(task.dueAt) || undefined,
          )
        : null;
      setDueAt(value as string);
    }
    onSave(patch);
  };

  const handleAddNote = () => {
    const trimmed = newNote.trim();
    if (!trimmed) return;

    const newNoteObj: TaskNote = {
      id: Date.now().toString(),
      content: trimmed,
      authorId: profile?.id || "",
      authorName: profile?.name || "Unknown",
      createdAt: new Date().toISOString(),
    };

    const updatedNotes = [newNoteObj, ...notes];
    setNotes(updatedNotes);
    setNewNote("");
    // Auto-save the comment immediately so the card's meta row updates without
    // requiring a separate Save click (matches the "send = one action" design).
    if (!isNewCard) {
      onSave({ notes: updatedNotes });
    } else {
      markDirty();
    }
    // Refocus composer so Enter key shortcut doesn't accidentally close the modal
    setTimeout(() => composerInputRef.current?.focus(), 0);
  };

  const handleDeleteNote = (id: string) => {
    const updatedNotes = notes.filter((n) => n.id !== id);
    setNotes(updatedNotes);
  };

  const handleEditNote = (noteObj: TaskNote) => {
    setEditingNoteId(noteObj.id);
    setEditingNoteText(noteObj.content);
    isEditingNoteRef.current = true;
  };

  const handleSaveNoteEdit = () => {
    if (!editingNoteId) return;
    const trimmed = editingNoteText.trim();
    if (!trimmed) {
      // If empty, delete the note
      const updatedNotes = notes.filter((n) => n.id !== editingNoteId);
      setNotes(updatedNotes);
    } else {
      // Just update local state, don't save to backend yet
      const updatedNotes = notes.map((n) =>
        n.id === editingNoteId
          ? { ...n, content: trimmed, updatedAt: new Date().toISOString() }
          : n,
      );
      setNotes(updatedNotes);
    }
    setEditingNoteId(null);
    setEditingNoteText("");
    // Delay resetting the ref to allow overlay click handler to check it first
    setTimeout(() => {
      isEditingNoteRef.current = false;
    }, 100);
  };

  const handleCancelNoteEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText("");
    // Delay resetting the ref to allow overlay click handler to check it first
    setTimeout(() => {
      isEditingNoteRef.current = false;
    }, 100);
  };

  const handleSelectExistingLabel = (existingLabel: {
    name: string;
    color: string;
  }) => {
    // Append (dedupe by name) up to the cap — selecting adds a label, never replaces.
    setLabels((prev) => {
      if (prev.some((l) => l.name === existingLabel.name)) return prev;
      if (prev.length >= MAX_TASK_LABELS) return prev;
      return [...prev, { name: existingLabel.name, color: existingLabel.color }];
    });
    setCustomLabelInput("");
    setShowLabelDropdown(false);
  };

  // Create (or select) a label with the given name/color.
  // Collapses the previously triplicated Enter / + button / swatch handlers;
  // preserves the exact ordering: add-to-module (try/catch) → select → clear → close.
  const handleCreateLabel = async (name: string, color: string) => {
    // Color is owned by the label registry (single source of truth): a label name
    // maps to exactly one color everywhere. If a label with this name already
    // exists, reuse its color rather than recoloring it.
    const existing = existingLabels.find((l) => l.name === name);
    const newLabel = { name, color: existing ? existing.color : color };
    // Add to module labels only if it's genuinely new
    if (!existing) {
      const updatedLabels = [...existingLabels, newLabel];
      // Save to module config immediately
      if (onUpdateModuleLabels) {
        try {
          await onUpdateModuleLabels(updatedLabels);
        } catch (err) {
          console.error("Failed to save label to module:", err);
        }
      }
    }
    // Append the label (dedupe by name) up to the cap — don't save task yet.
    setLabels((prev) => {
      if (prev.some((l) => l.name === newLabel.name)) return prev;
      if (prev.length >= MAX_TASK_LABELS) return prev;
      return [...prev, newLabel];
    });
    setCustomLabelInput("");
    setShowLabelDropdown(false);
  };

  const handleDeleteLabel = async (existingLabel: {
    name: string;
    color: string;
  }) => {
    const updatedLabels = existingLabels.filter(
      (l) => l.name !== existingLabel.name,
    );
    if (onUpdateModuleLabels) {
      try {
        await onUpdateModuleLabels(updatedLabels);
      } catch (err) {
        console.error("Failed to delete label from module:", err);
      }
    }
    // Remove it from this task's selected labels too, if applied.
    setLabels((prev) => prev.filter((l) => l.name !== existingLabel.name));
  };

  const renderDeleteConfirmationModal = () => {
    if (!deleteConfirmation) return null;

    return (
      <DeleteConfirmationModal
        onCancel={() => setDeleteConfirmation(null)}
        onConfirm={async () => {
          if (onDelete) {
            onDelete(deleteConfirmation.taskId);
          }
          setDeleteConfirmation(null);
          onClose();
        }}
      />
    );
  };

  // Use portal to render modal at document root, escaping any parent stacking contexts
  const modalContent = (
    <div
      className="task-modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.7)",
        zIndex: 2147483647,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        pointerEvents: "auto",
      }}
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        ref={taskModalContentRef}
        className="task-modal-content"
        style={{
          // One uniform modal card — responds to theme via CSS variables.
          background: "var(--color-surface)",
          borderRadius: 16,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          maxWidth: 512,
          width: "100%",
          maxHeight: "calc(100vh - 32px)",
          overflow: "visible",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flex: 1,
              minWidth: 0,
            }}
          >
            <h2
              style={{
                fontSize: 17,
                fontWeight: 500,
                color: "var(--color-text)",
                margin: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {viewOnly
                ? title || "Untitled Task"
                : isNewCard
                  ? "New Task"
                  : "Edit Task"}
            </h2>
            {columns.length > 0 && !isNewCard && (
              <StatusDropdown
                columns={columns}
                currentColumn={currentColumn}
                currentColumnLabel={currentColumnLabel}
                taskStatus={task.status}
                show={showStatusDropdown}
                onToggle={() => setShowStatusDropdown(!showStatusDropdown)}
                onSelect={handleStatusChange}
                dropdownRef={statusDropdownRef}
              />
            )}
          </div>
          <button
            onClick={handleSafeClose}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              background: "var(--color-surface-muted)",
              border: "none",
              borderRadius: "50%",
              color: "var(--color-text-muted)",
              fontSize: 28,
              lineHeight: 1,
              cursor: "pointer",
              flexShrink: 0,
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-surface-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--color-surface-muted)";
            }}
          >
            ×
          </button>
        </div>

        {viewOnly ? (
          <TaskViewOnlyBody
            task={task}
            owner={owner}
            dueAt={dueAt}
            notes={notes}
            conversationMembers={conversationMembers}
            showOwnerPicker={showOwnerPicker}
            showDatePicker={showDatePicker}
            setShowOwnerPicker={setShowOwnerPicker}
            setShowDatePicker={setShowDatePicker}
            onViewOnlySave={handleViewOnlySave}
          />
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            onInput={markDirty}
            onChange={markDirty}
            className="overflow-y-auto flex-1"
          >
            <div style={{ padding: "12px 20px 0" }}>
              <textarea
                value={title}
                onChange={(e) => {
                  const v = e.target.value;
                  setTitle(v.length > 0 ? v[0].toUpperCase() + v.slice(1) : v);
                }}
                placeholder="Card content"
                className="w-full px-3 py-2 border rounded-lg resize-none"
                style={{
                  background: "var(--color-surface)",
                  color: "var(--color-text)",
                  borderColor: "var(--color-border)",
                  outline: "none",
                }}
                rows={3}
                autoFocus
              />

              {(fieldOn('label') || fieldOn('dueDate')) && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
                  {fieldOn('label') && (
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text)" }}>
                        Label
                      </label>
                      {/* Selected labels (up to MAX_TASK_LABELS) as removable chips,
                          with an add trigger that opens the label dropdown. */}
                      <div
                        ref={labelFieldRef}
                        className="flex flex-wrap gap-2 items-center w-full py-2 px-3 border rounded-lg relative"
                        style={{ background: "var(--color-surface)", borderColor: "var(--color-border)" }}
                      >
                        {labels.map((l) => (
                          <span
                            key={l.name}
                            className="px-2.5 py-0.5 rounded-md text-xs font-medium flex items-center gap-1"
                            style={{ backgroundColor: l.color, color: "#23282e" }}
                          >
                            {l.name}
                            <button
                              type="button"
                              aria-label={`Remove ${l.name}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setLabels((prev) => prev.filter((x) => x.name !== l.name));
                              }}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#23282e", lineHeight: 1, padding: 0, fontSize: 14 }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {labels.length < MAX_TASK_LABELS ? (
                          <button
                            ref={labelButtonRef}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowLabelDropdown(!showLabelDropdown);
                            }}
                            className="text-sm"
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-muted)", padding: 0 }}
                          >
                            {labels.length === 0 ? "Add a label" : "+ Add label"}
                          </button>
                        ) : (
                          <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                            Max {MAX_TASK_LABELS} labels
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {fieldOn('dueDate') && (
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text)" }}>
                        Due date
                      </label>
                      <div className="task-due-row">
                        <DatePickerDropdown
                          value={dueAt}
                          onChange={setDueAt}
                          onClear={() => { setDueAt(""); setDueTime(""); }}
                        />
                        {dueAt && (
                          <TaskTimePicker value={dueTime} onChange={setDueTime} use24h={use24h} />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {fieldOn('label') && showLabelDropdown && (
                <LabelDropdown
                  dropdownRef={labelDropdownRef}
                  position={labelDropdownPosition}
                  existingLabels={existingLabels}
                  labelColors={labelColors}
                  customLabelInput={customLabelInput}
                  newLabelColor={newLabelColor}
                  hoveredLabel={hoveredLabel}
                  showDeleteButton={showDeleteButton}
                  hoverTimeoutRef={hoverTimeoutRef}
                  setCustomLabelInput={setCustomLabelInput}
                  setNewLabelColor={setNewLabelColor}
                  setHoveredLabel={setHoveredLabel}
                  setShowDeleteButton={setShowDeleteButton}
                  onSelectExisting={handleSelectExistingLabel}
                  onCreateLabel={handleCreateLabel}
                  onDeleteLabel={handleDeleteLabel}
                />
              )}

              {fieldOn('assignedTo') && conversationMembers.length >= 1 && (
                <div style={{ marginTop: 14 }}>
                  <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text)" }}>
                    Assignee
                  </label>
                  <div className="flex gap-2 items-center">
                    <select
                      value={owner?.id || ""}
                      onChange={(e) => {
                        const memberId = e.target.value;
                        if (!memberId) { setOwner(null); return; }
                        const member = conversationMembers.find((m: any) => m.id === memberId);
                        if (member) setOwner({ id: memberId, name: member.name || member.email });
                      }}
                      className="flex-1 px-3 py-2 border rounded-lg text-sm appearance-none"
                      style={{
                        background: "var(--color-surface)",
                        color: "var(--color-text)",
                        borderColor: "var(--color-border)",
                        backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                        backgroundPosition: "right 0.5rem center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "1.5em 1.5em",
                        paddingRight: "2.5rem",
                        outline: "none",
                      }}
                    >
                      <option value="">Unassigned</option>
                      {conversationMembers.map((member: any) => (
                        <option key={member.id} value={member.id}>{member.name || member.email}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOwner(null); }}
                      className="text-xl leading-none px-1"
                      style={{ color: "var(--color-text-muted)", visibility: owner ? "visible" : "hidden", background: "none", border: "none", cursor: "pointer" }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              {fieldOn('githubUrl') && (
                <div style={{ marginTop: 14 }}>
                  <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text)" }}>
                    GitHub Issue
                  </label>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="url"
                      value={githubUrl}
                      onChange={(e) => setGithubUrl(e.target.value)}
                      placeholder="https://github.com/org/repo/issues/123"
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      style={{ background: "var(--color-surface)", color: "var(--color-text)", borderColor: "var(--color-border)", outline: "none" }}
                    />
                    {githubUrl.trim() && (
                      <a href={githubUrl.trim()} target="_blank" rel="noopener noreferrer" title="Open issue"
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 7, flexShrink: 0, background: "var(--color-surface-hover)", border: "1px solid var(--color-border)", color: "var(--color-text-quiet)", textDecoration: "none" }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Description — a core field like the title, always available
                  (not gated by the notes/discussion field toggle). */}
              <div style={{ marginTop: 14, paddingBottom: 16 }}>
                <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text)" }}>
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a description"
                  className="w-full px-3 py-2 border rounded-lg resize-none"
                  style={{ background: "var(--color-surface)", color: "var(--color-text)", borderColor: "var(--color-border)", outline: "none" }}
                  rows={3}
                />
              </div>
            </div>

            {fieldOn('notes') && (
              <>
                <div style={{ borderTop: "1px solid var(--color-border)" }} />
                <div style={{ padding: "16px 20px 8px", background: "var(--color-surface-muted, #f8f7f5)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: notes.length > 0 ? 16 : 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "var(--color-text-faint, #9aa3ad)", textTransform: "uppercase" as const }}>Discussion</span>
                    {notes.length > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", background: "var(--color-surface-hover)", borderRadius: 4, padding: "1px 6px" }}>{notes.length}</span>
                    )}
                  </div>

                  {/* Thread, oldest first — notes are stored newest-first, so reverse for display. */}
                  {[...notes].reverse().map((noteObj) => (
                    <div key={noteObj.id} style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-start" }}>
                      <div style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, background: getAvatarColorPair(noteObj.authorName).bg, color: getAvatarColorPair(noteObj.authorName).text }}>
                        {avatarInitials(noteObj.authorName)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text)" }}>{noteObj.authorName}</span>
                          <span style={{ fontSize: 12, color: "var(--color-text-muted)" }} title={new Date(noteObj.createdAt).toLocaleString()}>{relativeTime(noteObj.createdAt)}</span>
                          {noteObj.authorId === profile?.id && editingNoteId !== noteObj.id && (
                            <span style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                              <button type="button" onClick={() => handleEditNote(noteObj)} style={{ fontSize: 11, color: "var(--color-text-muted)", background: "none", border: "none", cursor: "pointer", padding: "0 2px" }}>Edit</button>
                              <button type="button" onClick={() => handleDeleteNote(noteObj.id)} style={{ fontSize: 11, color: "var(--color-text-muted)", background: "none", border: "none", cursor: "pointer", padding: "0 2px" }}>Delete</button>
                            </span>
                          )}
                        </div>
                        {editingNoteId === noteObj.id ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <textarea
                              value={editingNoteText}
                              onChange={(e) => setEditingNoteText(e.target.value)}
                              rows={2}
                              autoFocus
                              className="w-full px-3 py-2 border rounded-lg resize-none text-sm"
                              style={{ background: "var(--color-surface)", color: "var(--color-text)", borderColor: "var(--color-border)", outline: "none" }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSaveNoteEdit(); }
                                if (e.key === "Escape") { e.preventDefault(); handleCancelNoteEdit(); }
                              }}
                            />
                            <div style={{ display: "flex", gap: 6 }}>
                              <button type="button" onClick={handleSaveNoteEdit} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 5, background: "var(--color-brand, #15a5e3)", border: "none", color: "#fff", cursor: "pointer" }}>Save</button>
                              <button type="button" onClick={handleCancelNoteEdit} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 5, background: "none", border: "1px solid var(--color-border)", color: "var(--color-text-muted)", cursor: "pointer" }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <p style={{ fontSize: 13, color: "var(--color-text)", margin: 0, lineHeight: "1.5", whiteSpace: "pre-wrap" }}>{noteObj.content}</p>
                        )}
                      </div>
                    </div>
                  ))}

                  <div style={{ display: "flex", gap: 10, alignItems: "center", paddingTop: notes.length > 0 ? 4 : 0, paddingBottom: 8 }}>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, background: getAvatarColorPair(profile?.name || "Me").bg, color: getAvatarColorPair(profile?.name || "Me").text }}>
                      {avatarInitials(profile?.name || "Me")}
                    </div>
                    <input
                      ref={composerInputRef}
                      type="text"
                      value={newNote}
                      onChange={(e) => { setNewNote(e.target.value); e.stopPropagation(); }}
                      onInput={(e) => e.stopPropagation()}
                      placeholder="Write a comment..."
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      style={{ background: "var(--color-surface)", color: "var(--color-text)", borderColor: "var(--color-border)", outline: "none" }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); handleAddNote(); }
                        if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setNewNote(""); composerInputRef.current?.blur(); }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddNote(); }}
                      style={{ width: 34, height: 34, borderRadius: 8, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: newNote.trim() ? "var(--color-brand, #15a5e3)" : "var(--color-border)", color: newNote.trim() ? "#fff" : "var(--color-text-muted)", transition: "background 0.15s" }}
                      disabled={!newNote.trim()}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="19" x2="12" y2="5"></line>
                        <polyline points="5 12 12 5 19 12"></polyline>
                      </svg>
                    </button>
                  </div>
                </div>
              </>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 20px 20px 20px",
                borderBottomLeftRadius: "12px",
                borderBottomRightRadius: "12px",
              }}
            >
              <div style={{ display: "flex", gap: "8px" }}>
                {!isNewCard && task?.id && onArchive && (
                  <button
                    type="button"
                    onClick={() => {
                      onArchive(task.id);
                      onClose();
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      background: "transparent",
                      border: "none",
                      borderRadius: 6,
                      color: "#9aa1a8",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "var(--color-action-archive-bg)";
                      e.currentTarget.style.color =
                        "var(--color-action-archive-text)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "#9aa1a8";
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 8v13H3V8"></path>
                      <path d="M1 3h22v5H1z"></path>
                      <path d="M10 12h4"></path>
                    </svg>
                  </button>
                )}
              </div>
              <div style={{ display: "flex", gap: "12px", marginLeft: "auto" }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    background: "var(--color-action-cancel-bg)",
                    border: "1px solid var(--color-action-cancel-border)",
                    color: "var(--color-action-cancel-text)",
                    minWidth: "80px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "var(--color-action-cancel-bg-hover)";
                    e.currentTarget.style.borderColor =
                      "var(--color-action-cancel-border-hover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "var(--color-action-cancel-bg)";
                    e.currentTarget.style.borderColor =
                      "var(--color-action-cancel-border)";
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: "8px 16px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    // Brand-blue Save on the cool panel (per mock 2026-06-10).
                    background: "var(--color-brand, #15a5e3)",
                    border: "none",
                    color: "#ffffff",
                    minWidth: "80px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "var(--color-brand-hover, #1391c9)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "var(--color-brand, #15a5e3)";
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  // Render to dedicated modal portal root to escape any parent stacking contexts
  const portalRoot = document.getElementById("modal-portal-root");
  if (!portalRoot) {
    console.error("Modal portal root not found");
    return null;
  }
  return (
    <Fragment>
      {createPortal(modalContent, portalRoot)}
      {deleteConfirmation &&
        createPortal(renderDeleteConfirmationModal(), document.body)}
      {createPortal(
        <UnsavedChangesGuardModal
          show={showUnsavedConfirmation}
          onCancel={() => setShowUnsavedConfirmation(false)}
          onDiscard={onClose}
          // requestSubmit fires the form's onSubmit (handleSubmit), which saves
          // and then closes via onSave/onClose — same path as the Save button.
          onSave={() => formRef.current?.requestSubmit()}
        />,
        portalRoot,
      )}
    </Fragment>
  );
}
